import React, { useState, useCallback, useRef, useEffect } from 'react'

import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar'
import PageHeader from '@shared/PageHeader'
import useSmartBack from '@hooks/shared/useSmartBack'
import { QuestionCircleOutlined } from '@ant-design/icons'
import {
  Form,
  Input,
  InputNumber,
  Button,
  message,
  Radio,
  Checkbox,
  Drawer,
  Row,
  Space,
  List,
  Tag,
  Tooltip,
  Alert,
} from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { stockTakingService } from '@services/operator/stockTakingService'
import ShelfTransferSelector from './components/ShelfTransferSelector'

export default function StockTakingNew() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const goBack = useSmartBack()
  const [form] = Form.useForm()

  const [submitting, setSubmitting] = useState(false)
  const [selectedCount, setSelectedCount] = useState(0)
  const [shelfSelectorKey, setShelfSelectorKey] = useState(0)
  const shelfSelectorRef = useRef()

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false)

  // Split state
  const [splitEnabled, setSplitEnabled] = useState(false)
  const [splitMode, setSplitMode] = useState('zone')
  const [splitSize, setSplitSize] = useState(100)
  const [previewGroups, setPreviewGroups] = useState([])
  const [previewLoading, setPreviewLoading] = useState(false)

  // 打開 Drawer 前先檢查有沒有選儲位
  const handleOpenDrawer = useCallback(() => {
    const count = shelfSelectorRef.current?.getSelectedIds()?.length ?? 0
    if (count === 0) {
      message.warning(t('shared.stock_takings.new.select_shelves_warning'))
      return
    }
    setDrawerVisible(true)
  }, [t])

  // 取得預覽分組
  const fetchPreview = useCallback(async (mode, size) => {
    const shelfIds = shelfSelectorRef.current?.getSelectedIds() ?? []
    if (shelfIds.length === 0) return

    setPreviewLoading(true)
    try {
      const res = await stockTakingService.previewSplit({
        shelf_ids: shelfIds,
        split_mode: mode,
        split_size: mode === 'quantity' ? size : undefined,
      })
      if (res.success) {
        setPreviewGroups(res.data.groups)
      }
    } catch (error) {
      // silent fail for preview
    } finally {
      setPreviewLoading(false)
    }
  }, [])

  // 當切分設定變更時重新預覽
  useEffect(() => {
    if (splitEnabled && drawerVisible) {
      fetchPreview(splitMode, splitSize)
    }
  }, [splitEnabled, splitMode, splitSize, drawerVisible, fetchPreview])

  const groupCount = splitEnabled ? previewGroups.length : 1

  // 提交建立
  const handleConfirmCreate = useCallback(async (andAddAnother = false) => {
    const shelfIds = shelfSelectorRef.current?.getSelectedIds() ?? []
    if (shelfIds.length === 0) return

    try {
      await form.validateFields()
    } catch (e) {
      return
    }

    setSubmitting(true)
    try {
      const values = form.getFieldsValue()
      const payload = {
        stock_taking: {
          is_blind: values.blind_type === 'blind',
          mode: values.mode || 'dynamic',
          note: values.note,
        },
        shelf_ids: shelfIds,
        split_mode: splitEnabled ? splitMode : null,
        split_size: splitEnabled && splitMode === 'quantity' ? splitSize : null,
      }

      const response = await stockTakingService.batchCreate(payload)

      if (response.success) {
        const count = response.data.count
        if (andAddAnother) {
          message.success(
            count > 1
              ? t('shared.stock_takings.drawer.batch_create_success', { count })
              : t('common.message.create_success')
          )
          // 重置
          setDrawerVisible(false)
          form.resetFields()
          setSplitEnabled(false)
          setPreviewGroups([])
          setShelfSelectorKey(k => k + 1)
          setSelectedCount(0)
        } else {
          if (count === 1) {
            message.success(t('common.message.create_success'))
            navigate(`/stock_takings/${response.data.stock_takings[0].id}`, { replace: true })
          } else {
            message.success(t('shared.stock_takings.drawer.batch_create_success', { count }))
            navigate('/stock_takings', { replace: true })
          }
        }
      } else {
        message.error(response.message || t('common.error.create_failed'))
      }
    } catch (error) {
      console.error('Create stock taking error:', error)
      message.error(t('common.error.network_error'))
    } finally {
      setSubmitting(false)
    }
  }, [form, splitEnabled, splitMode, splitSize, navigate, t])

  return (
    <div>
      <PageHeader items={['stocks', 'stocks.stock_takings', { label: t('shared.stock_takings.new.title') }]} />

      {/* 儲位選擇 */}
      <ShelfTransferSelector
        key={shelfSelectorKey}
        ref={shelfSelectorRef}
        onCountChange={setSelectedCount}
      />

      <FloatingActionBarSpacer />
      <FloatingActionBar
        role="operator"
        onCancel={goBack}
        onSubmit={handleOpenDrawer}
        submitText={`${t('shared.stock_takings.new.create_button')} (${selectedCount})`}
        submitDisabled={selectedCount === 0}
      />

      {/* 設定 Drawer */}
      <Drawer
        title={t('shared.stock_takings.drawer.title')}
        placement="right"
        width={500}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        forceRender
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => setDrawerVisible(false)}>
                {t('common.button.cancel')}
              </Button>
              <Button
                onClick={() => handleConfirmCreate(true)}
                loading={submitting}
              >
                {t('shared.stock_takings.drawer.save_and_select_more')}
              </Button>
              <Button
                type="primary"
                onClick={() => handleConfirmCreate(false)}
                loading={submitting}
              >
                {splitEnabled && groupCount > 1
                  ? t('shared.stock_takings.drawer.confirm_button', { count: groupCount })
                  : t('shared.stock_takings.drawer.confirm_button_single')
                }
              </Button>
            </Space>
          </Row>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ blind_type: 'normal', mode: 'dynamic' }}
        >
          {/* 盤點類型（明盤/盲盤） */}
          <Form.Item
            name="blind_type"
            label={
              <Space size={4}>
                {t('models.stock_taking.is_blind')}
                <Tooltip
                  title={
                    <div>
                      <div><strong>{t('shared.stock_takings.new.blind_type_normal')}:</strong> {t('shared.stock_takings.new.blind_type_normal_desc')}</div>
                      <div style={{ marginTop: 4 }}><strong>{t('shared.stock_takings.new.blind_type_blind')}:</strong> {t('shared.stock_takings.new.blind_type_blind_desc')}</div>
                    </div>
                  }
                >
                  <QuestionCircleOutlined style={{ color: '#999', cursor: 'pointer' }} />
                </Tooltip>
              </Space>
            }
          >
            <Radio.Group>
              <Radio.Button value="normal">
                {t('shared.stock_takings.new.blind_type_normal')}
              </Radio.Button>
              <Radio.Button value="blind">
                {t('shared.stock_takings.new.blind_type_blind')}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* 盤點模式（動態/靜態） */}
          <Form.Item
            name="mode"
            label={
              <Space size={4}>
                {t('models.stock_taking.mode')}
                <Tooltip
                  title={
                    <div>
                      <div>
                        <strong>{t('shared.stock_takings.new.mode_dynamic')}:</strong> {t('shared.stock_takings.new.mode_dynamic_desc')}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <strong>{t('shared.stock_takings.new.mode_static')}:</strong> {t('shared.stock_takings.new.mode_static_desc')}
                      </div>
                    </div>
                  }
                >
                  <QuestionCircleOutlined style={{ color: '#999', cursor: 'pointer' }} />
                </Tooltip>
              </Space>
            }
          >
            <Radio.Group>
              <Radio.Button value="dynamic">
                {t('shared.stock_takings.new.mode_dynamic')}
              </Radio.Button>
              <Radio.Button value="static">
                {t('shared.stock_takings.new.mode_static')}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* 備註 */}
          <Form.Item
            name="note"
            label={t('models.stock_taking.note')}
          >
            <Input.TextArea
              rows={2}
              placeholder={t('shared.stock_takings.new.note_placeholder')}
            />
          </Form.Item>

          {/* 切分設定 */}
          <div style={{ marginBottom: 16 }}>
            <Space size={4}>
              <Checkbox
                checked={splitEnabled}
                onChange={(e) => setSplitEnabled(e.target.checked)}
              >
                {t('shared.stock_takings.drawer.split_checkbox')}
              </Checkbox>
              <Tooltip title={t('shared.stock_takings.drawer.split_checkbox_tooltip')}>
                <QuestionCircleOutlined style={{ color: '#999', cursor: 'pointer' }} />
              </Tooltip>
            </Space>
          </div>

          {splitEnabled && (
            <div style={{ padding: '0 0 0 24px' }}>
              <Form.Item label={t('shared.stock_takings.drawer.split_mode_label')}>
                <Radio.Group
                  value={splitMode}
                  onChange={(e) => setSplitMode(e.target.value)}
                >
                  <Radio value="zone">
                    <Space size={4}>
                      {t('shared.stock_takings.drawer.split_mode_zone')}
                      <Tooltip title={t('shared.stock_takings.drawer.split_mode_zone_tooltip')}>
                        <QuestionCircleOutlined style={{ color: '#999', cursor: 'pointer' }} />
                      </Tooltip>
                    </Space>
                  </Radio>
                  <Radio value="quantity">
                    <Space size={4}>
                      {t('shared.stock_takings.drawer.split_mode_quantity')}
                      <Tooltip title={t('shared.stock_takings.drawer.split_mode_quantity_tooltip')}>
                        <QuestionCircleOutlined style={{ color: '#999', cursor: 'pointer' }} />
                      </Tooltip>
                    </Space>
                  </Radio>
                </Radio.Group>
              </Form.Item>

              {splitMode === 'quantity' && (
                <Form.Item label={t('shared.stock_takings.drawer.split_size_label')}>
                  <InputNumber
                    min={1}
                    value={splitSize}
                    onChange={(val) => setSplitSize(val || 100)}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              )}

              {/* 預覽 */}
              <div style={{ marginTop: 8 }}>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>
                  {t('shared.stock_takings.drawer.preview_title')}
                </div>
                <List
                  size="small"
                  loading={previewLoading}
                  dataSource={previewGroups}
                  renderItem={(group, idx) => (
                    <List.Item>
                      <span>
                        {splitMode === 'zone'
                          ? (group.label || t('shared.stock_takings.drawer.preview_no_zone'))
                          : `${t('shared.stock_takings.drawer.preview_batch_label')} ${idx + 1}`
                        }
                      </span>
                      <Tag>{group.shelf_count} {t('shared.stock_takings.new.shelves_unit')}</Tag>
                    </List.Item>
                  )}
                  footer={
                    previewGroups.length > 0 && (
                      <div style={{ textAlign: 'center', color: '#999', fontSize: 12 }}>
                        {t('shared.stock_takings.drawer.preview_groups', { count: previewGroups.length })}
                      </div>
                    )
                  }
                />
                {splitMode === 'zone' && previewGroups.length <= 1 && previewGroups[0]?.zone_id === null && (
                  <Alert
                    type="warning"
                    showIcon
                    message={t('shared.stock_takings.drawer.no_zone_split_warning')}
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
            </div>
          )}
        </Form>
      </Drawer>
    </div>
  )
}
